import { plainToInstance, Type } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import { IPermitTemplate } from './IPermitTemplate'
import { User } from '@core/entities/user'
import { PermitTemplateVersion } from '@core/entities/permit-template-editor'
import { IFolderPath } from '@core/entities/folders/FolderPath'
import { PermitTemplateStatus } from '@core/entities/permit/PermitTemplate/types'
import { ProcessTagsWrapper } from '@core/entities/processTags/ProcessTagsWrapper'
import { statuses } from '@core/theme/statuses'

type PermitTemplatePermission =
  | 'READ'
  | 'EDIT'
  | 'OVERRIDE'
  | 'CLONE_VERSION'
  | 'EDIT_TEMPLATE_VERSION'
  | 'MANAGE_PROJECTS'
  | 'UPDATE_TEMPLATE_SETTINGS'

export class PermitTemplate extends Entity<IPermitTemplate> {
  id: number
  organisationName: string
  organisationId: number
  name: string
  code: string
  colour: string
  description: string
  requesterConfirmationText: string
  approverConfirmationText: string
  published: boolean

  createdOn: string
  modifiedOn: string
  @Type(() => User)
  createdBy: User
  @Type(() => User)
  modifiedBy: User

  requireGeolocationForBriefings: boolean
  requireGeolocationForPermit: boolean
  requireGeolocationForPostPermitChecks: boolean
  geoLocationInputType: 'WHAT_3_WORDS' | 'DRAW_ON_MAP'

  status: PermitTemplateStatus
  timezone: string

  useInReviewVersion: boolean
  permittedActions: PermitTemplatePermission[]

  @Type(() => PermitTemplateVersion)
  currentVersion?: PermitTemplateVersion

  folder?: {
    folderId: number
    pathFromRoot?: IFolderPath
  }

  @Type(() => ProcessTagsWrapper)
  processTags?: ProcessTagsWrapper

  /**
   * When true, a permit created from this template MUST be linked to a parent
   * permit. The create-list endpoint returns this on the template root (other
   * config flags like `requireGeolocationForPermit` follow the same pattern),
   * and the create-permit form uses it to decide whether to render the parent
   * picker. Also exposed on `IPermitTemplateVersion` for completeness.
   */
  requireParentPermit?: boolean

  public static statusColor(status: PermitTemplateStatus) {
    switch (status) {
      case 'PUBLISHED':
        return statuses.published.color
      case 'UNPUBLISHED':
        return statuses.unpublished.color
      case 'ARCHIVED':
        return statuses.archived.color
      default:
        return 'default'
    }
  }

  public static statusText(status: PermitTemplateStatus) {
    switch (status) {
      case 'PUBLISHED':
        return statuses.published.label
      case 'UNPUBLISHED':
        return statuses.unpublished.label
      case 'ARCHIVED':
        return statuses.archived.label
      default:
        return ''
    }
  }

  private hasPermission(permission: PermitTemplatePermission): boolean {
    return this.permittedActions.includes(permission)
  }

  public canEdit() {
    return this.hasPermission('EDIT')
  }

  public canManageTemplateVersion() {
    return this.canEdit() || this.hasPermission('EDIT_TEMPLATE_VERSION')
  }

  public canManageTemplateSettings() {
    return this.canEdit() || this.hasPermission('UPDATE_TEMPLATE_SETTINGS')
  }

  public canManageProjects() {
    return this.canEdit() || this.hasPermission('MANAGE_PROJECTS')
  }

  public static new(payload: unknown): PermitTemplate {
    return plainToInstance(PermitTemplate, payload)
  }
}
